import {
  createAsyncThunk, createSlice, EntityState, PayloadAction,
} from '@reduxjs/toolkit';
import { fetchAdminLayouts, putLayout } from 'api/layout.service';
import { normalize } from 'normalizr';
import { notificationActions } from 'redux/notification/notification.slice';
import { AxiosError } from 'axios';
import {
  LayoutEditResponse, LayoutEntry, Problem, RoleModel,
} from 'types/entity';
import { LayoutEditSchema } from 'types/schema/layout-schema';
import { layoutSchema, layoutsAdapter } from './layout-update.adapter';

export const LAYOUT_UPDATE_STORE_NAME = 'layoutUpdate';

export const loadLayouts = createAsyncThunk(
  `${LAYOUT_UPDATE_STORE_NAME}/load`,
  (projectId: string, thunkAPI) => fetchAdminLayouts(projectId)
    .then((res) => normalize(res, [layoutSchema]))
    .then((normalized) => normalized.entities) as Promise<LayoutEditSchema>,
);

export const saveLayout = createAsyncThunk(
  `${LAYOUT_UPDATE_STORE_NAME}/save`,
  async (payload: {
    projectId: string, layoutId: string, layout: LayoutEntry
  }, thunkAPI) => {
    const { projectId, layoutId, layout } = payload;
    const res = await putLayout(projectId, layoutId, layout).catch((error) => {
      const errorResponse = (error as AxiosError).response;
      const errorProblem = errorResponse?.data as Problem;
      throw new Error(errorProblem.type);
    });
    thunkAPI.dispatch(notificationActions.addSuccess({ messageId: 'notification.layoutUpdate.success' }));
    return res;
  },
);

export type LayoutUpdateState = EntityState<LayoutEditResponse> & {
  selectedId: string
  loaded: boolean
  touched: boolean
  processing: boolean
}

export const initialLayoutUpdateState: LayoutUpdateState = layoutsAdapter.getInitialState({
  selectedId: '',
  loaded: false,
  touched: false,
  processing: false,
});

export const layoutUpdateSlice = createSlice({
  name: LAYOUT_UPDATE_STORE_NAME,
  initialState: initialLayoutUpdateState,
  // awaitでないAction系
  reducers: {
    selectOne: (state, action: PayloadAction<string>) => {
      state.selectedId = action.payload;
    },
    updateGadgetRef: (state, action: PayloadAction<LayoutEditSchema>) => {
      layoutsAdapter.upsertMany(state, action.payload.layouts || {});
      state.touched = true;
    },
    updateOne: (state, action: PayloadAction<{layoutId: string, roles: RoleModel[]}>) => {
      const newst: LayoutEditResponse = {
        ...state.entities[action.payload.layoutId],
        roles: action.payload.roles,
      };
      layoutsAdapter.updateOne(state, {
        id: action.payload.layoutId,
        changes: newst,
      });
      state.touched = true;
    },
    clear: (state, _action: PayloadAction) => {
      state.selectedId = '';
      state.processing = false;
      state.touched = false;
    },
    unTouched: (state) => {
      state.touched = false;
    },
  },
  // awaitするAction系
  extraReducers: (builder) => {
    // ロード
    builder.addCase(loadLayouts.pending, (state) => {
      layoutsAdapter.removeAll(state);
      state.loaded = false;
    });
    builder.addCase(loadLayouts.fulfilled, (state, action) => {
      layoutsAdapter.addMany(state, action.payload.layouts || {});
      state.loaded = true;
    });
    builder.addCase(loadLayouts.rejected, (state, action) => {
      state.loaded = true;
    });
    // 更新
    builder.addCase(saveLayout.pending, (state) => {
      state.processing = true;
    });
    builder.addCase(saveLayout.fulfilled, (state, action) => {
      state.processing = false;
    });
    builder.addCase(saveLayout.rejected, (state, action) => {
      state.processing = false;
    });
  },
});

// Action creators are generated for each case reducer function
export const layoutUpdateActions = {
  ...layoutUpdateSlice.actions,
  loadLayouts,
  saveLayout,
};

export default layoutUpdateSlice.reducer;
